import { RequiredAsterisk } from 'components/forms/inputs/rhf-inputs/InputWrapper';
import { FIELD_LABEL_CLASS } from 'components/GridItemList';
import { ModalFooter } from 'components/modals/ModalFooter';
import { ModalHeader } from 'components/modals/ModalHeader';
import PartyName from 'components/party/PartyName';
import { RecordLink } from 'components/record-chip/RecordLink';
import { DelegationAlert } from 'pages/delegations/DelegationAlert';
import { useId, useState } from 'react';
import { useUpdateAssessmentMutation } from 'store/api/client-identifications';
import { ClientIdentificationDto, TierCode } from 'types/clientIdentification';
import { PartyType } from 'types/contacts';
import { t } from 'utils/getTranslations';
import { assessmentUpdate } from '../assessmentUpdate';
import { useTierOptions } from '../tierOptions';
import { TierRadioCards } from '../TierRadioCards';
import { Value, VALUE_GRID_CLASS } from './Block';
import { successAlert } from 'utils/alert';

export interface ClientParty {
    id: number;
    name: string;
    partyType: PartyType;
}

interface Props {
    client: ClientParty;
    assessment: ClientIdentificationDto;
}

export function ClientAndTierBlock({ client, assessment }: Props) {
    const { options } = useTierOptions();
    const tier = options.find((option) => option.code === assessment.tierCode);
    return (
        <div className={VALUE_GRID_CLASS}>
            <Value label={t('client-identification.client')}>
                <PartyName party={client} isLink renderPartyIcon variant="readonly-other" />
            </Value>
            <Value label={t('client-identification.tier')} detail={tier?.caption}>
                {tier?.name ?? assessment.tierCode}
            </Value>
            <Value label={t('client-identification.identified-by')}>{assessment.identifiedByName}</Value>
            <Value label={t('client-identification.responsible-person')}>{assessment.responsibleName}</Value>
            {assessment.supersedesContractId ? (
                <Value label={t('client-identification.replaces')}>
                    <RecordLink
                        to={`/contract/${assessment.supersedesContractId}`}
                        label={t('client-identification.earlier-version')}
                    />
                </Value>
            ) : null}
            {assessment.supersededByContractId ? (
                <Value label={t('client-identification.replaced-by')}>
                    <RecordLink
                        to={`/contract/${assessment.supersededByContractId}`}
                        label={t('client-identification.newer-version')}
                    />
                </Value>
            ) : null}
        </div>
    );
}

interface EditProps {
    contractId: number;
    client: ClientParty;
    assessment: ClientIdentificationDto;
    onClose: () => void;
}

export function ClientAndTierEditModal({ contractId, client, assessment, onClose }: EditProps) {
    // oxlint-disable-next-line react-doctor/no-derived-useState -- the popup edits a copy, stored only on Save
    const [tierCode, setTierCode] = useState<TierCode>(assessment.tierCode);
    const tierLabelId = useId();
    const [updateAssessment, { isLoading }] = useUpdateAssessmentMutation();

    const save = async () => {
        if (tierCode === assessment.tierCode) {
            onClose();
            return;
        }
        try {
            await updateAssessment({ id: contractId, ...assessmentUpdate(assessment, { tierCode }) }).unwrap();
        } catch {
            // The API layer has shown the server's reason; the popup stays open.
            return;
        }
        successAlert(t('contracts.contract-updated'));
        onClose();
    };

    return (
        <>
            <ModalHeader title={t('client-identification.block.client-and-tier')} onCancel={onClose} />
            <div className="flex flex-col gap-4 overflow-y-auto p-6">
                <Value label={t('client-identification.client')}>
                    <PartyName party={client} renderPartyIcon variant="readonly-other" />
                </Value>
                <div className="flex flex-col gap-1.5">
                    <span id={tierLabelId} className={FIELD_LABEL_CLASS}>
                        {t('client-identification.tier')}
                        <RequiredAsterisk />
                    </span>
                    <TierRadioCards value={tierCode} onChange={setTierCode} labelledBy={tierLabelId} />
                </div>
                <div className={VALUE_GRID_CLASS}>
                    <Value label={t('client-identification.identified-by')}>{assessment.identifiedByName}</Value>
                    <Value label={t('client-identification.responsible-person')}>{assessment.responsibleName}</Value>
                </div>
                {tierCode !== assessment.tierCode ? (
                    <DelegationAlert severity="warning" role="status" className="items-start">
                        <span className="font-normal">{t('client-identification.tier-change-hint')}</span>
                    </DelegationAlert>
                ) : null}
            </div>
            <ModalFooter onCancel={onClose} onSave={() => void save()} disableSave={isLoading} />
        </>
    );
}
